// THE FLAP-OUT SIDE AGENT — one shell, every surface. The workbench's chat panel grew the
// full apparatus (fold spine, chat-client thread that sticks to the foot and never yanks a
// reader, the live activity band, the growing composer), and the form modals then got a
// second, poorer copy. This component is the ONE implementation: hosts own their state —
// what a turn sends, what a reply means, what cards ride on a message (proposal cards with
// Hold in the bench, take-into-field blocks in the modals, via `extras`) — and this owns the
// shell and its behaviours, so the flap-out looks and acts the same wherever it appears.
//
// Width comes from the host's context: the panel reads --rw-chat-w (the bench sets it on
// .rw-wb, the compose modals on .del-compose), so the same classes serve both.
import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import type { TurnActivity } from '../api/agent.js';
import ChatActivity from './ChatActivity.js';
import { useT } from '../i18n/index.js';
import './AgentFlyout.de.js';
import './AgentFlyout.fr.js';

/** How close to the foot of the thread still counts as watching it — past this, nothing yanks. */
const NEAR_FOOT = 72;

export interface FlyoutMsg {
  who: 'op' | 'agent';
  text: string;
  /** Cards riding on an agent message — the host's own meaning (proposals, field blocks…). */
  extras?: ReactNode;
}

export function AgentFlyout({
  open, onFold, label, byline, sessionLabel, headExtra, overlay, emptyState,
  msgs, busy, activity, busyStartedAt, ask, onAskChange, onSend, placeholder, sendDisabled,
  focusSignal, footSignal,
}: {
  open: boolean;
  onFold: (open: boolean) => void;
  /** The spine's and head's name — "Refine with the agent", "Draft with the agent". */
  label: string;
  /** The one-line truth under every agent turn — what this agent may and may not do. */
  byline: string;
  /** The head's right-hand session note — "run #12", "New conversation". */
  sessionLabel?: ReactNode;
  /** Extra head controls (the bench's Conversations switcher). */
  headExtra?: ReactNode;
  /** A host-owned layer over the stack (the bench's conversation list). */
  overlay?: ReactNode;
  /** What an empty thread says. */
  emptyState?: ReactNode;
  msgs: FlyoutMsg[];
  busy: boolean;
  /** The live doing behind a blocking turn — null shows the clock alone. */
  activity: TurnActivity | null;
  busyStartedAt: number;
  ask: string;
  onAskChange: (v: string) => void;
  onSend: () => void;
  placeholder: string;
  sendDisabled?: boolean;
  /** Bump to focus the composer (a host action pre-filled the ask — “Adapt with the agent”). */
  focusSignal?: number;
  /** Bump to snap the thread to its foot (a host swapped the whole thread — a reopened run). */
  footSignal?: number;
}) {
  const t = useT();
  /** Was the operator at the foot of the thread when the turn landed? Only then do we follow. */
  const stick = useRef(true);
  const [atFoot, setAtFoot] = useState(true);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const askRef = useRef<HTMLTextAreaElement | null>(null);

  const onThreadScroll = useCallback(() => {
    const el = threadRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_FOOT;
    stick.current = near;
    setAtFoot(near);
  }, []);
  const toFoot = useCallback((smooth: boolean) => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    stick.current = true;
    setAtFoot(true);
  }, []);
  /** Follow a new turn ONLY from the foot. Scrolled up to read? Then nothing moves under you. */
  useLayoutEffect(() => {
    if (!open || !stick.current) return;
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs, busy, open]);
  /** The composer grows a few lines, then scrolls inside itself — the foot never eats the thread. */
  useLayoutEffect(() => {
    const el = askRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [ask, open]);
  useLayoutEffect(() => { if (focusSignal) askRef.current?.focus(); }, [focusSignal]);
  useLayoutEffect(() => {
    if (!footSignal) return;
    stick.current = true;
    toFoot(false);
  }, [footSignal, toFoot]);

  return (
    <section className={`rw-chat${open ? '' : ' folded'}`} aria-label={label}>
      {!open ? (
        <button type="button" className="rw-chat-spine" aria-expanded={false} title={t('Fold the chat out')} onClick={() => onFold(true)}>
          <span className="ic" aria-hidden="true">›</span>
          <span className="lbl">{label}</span>
          {msgs.length ? <span className="n voice-figure">{msgs.length}</span> : null}
        </button>
      ) : null}
      <div className="rw-chat-panel">
        <div className="rw-chat-head">
          <button type="button" className="rw-chat-fold" aria-expanded={open} title={t('Fold the chat in')} onClick={() => onFold(false)}>‹</button>
          {headExtra ?? <span className="u-label">{label}</span>}
          {sessionLabel !== undefined ? <span className="sess src-ref">{sessionLabel}</span> : null}
        </div>

        <div className="rw-chat-stack">
          <div className="rw-chat-thread" ref={threadRef} onScroll={onThreadScroll}>
            <div className="rw-chat-log">
              {msgs.length === 0 && emptyState ? <div className="rw-chat-blank">{emptyState}</div> : null}
              {msgs.map((m, i) => m.who === 'op' ? (
                <div className="rw-msg-op voice-record" key={i}>{m.text}</div>
              ) : (
                <div className="rw-msg-ag" key={i}>
                  <div className="who">{byline}</div>
                  {m.text ? <div style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div> : null}
                  {m.extras}
                </div>
              ))}
              {busy ? (
                <div className="rw-msg-ag">
                  <div className="who">{byline}</div>
                  <ChatActivity activity={activity} startedAt={busyStartedAt} />
                </div>
              ) : null}
            </div>
          </div>

          <div className="rw-chat-foot">
            {!atFoot && msgs.length ? (
              <button type="button" className="rw-chat-jump" onClick={() => toFoot(true)}>{t('Jump to the latest')} ↓</button>
            ) : null}
            <div className="rw-ask">
              <textarea
                ref={askRef}
                className="rw-askbox"
                rows={1}
                value={ask}
                placeholder={placeholder}
                title={t('Enter sends · Shift+Enter starts a new line')}
                onChange={(e) => onAskChange(e.target.value)}
                onKeyDown={(e) => {
                  // Enter commits the turn; Shift+Enter (and a live IME composition) does not.
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); onSend(); }
                }}
                disabled={busy || sendDisabled}
              />
              <button type="button" className="btn btn-sm btn-accent" disabled={busy || sendDisabled || !ask.trim()} onClick={onSend}>
                {busy ? '…' : t('Ask →')}
              </button>
            </div>
          </div>

          {overlay}
        </div>
      </div>
    </section>
  );
}

export default AgentFlyout;
